import { useEffect, useState } from "react";

import useCreditCardEntryState from "./state";
import { useCCValidations } from "./useCCValidations";

import useImportScript from "Utilities/useImportScript";

export default function WePay({
  children,
  getPaymentMethodTokenTrigger = false,
  setPaymentMethodTokenInfo,
  setInputErrors,
}) {
  const wePayLoadedCallback = () => {
    const myAppId = window.data.paymentPageData.appID;
    const environment = window.data.paymentPageData.environment;

    const apiVersion = "3.0";

    let error;
    if (environment === "api") {
      error = window.WePay.configure("production", myAppId, apiVersion); //prod
    } else {
      error = window.WePay.configure("stage", myAppId, apiVersion); //stage
    }

    if (error) {
      alert(
        "Hmmm... something went wrong.  Please refresh the page and try again.  If it happens again, please email help@flocknote.com.  Error:wp_config"
      );
    }
  };

  useImportScript("https://cdn.wepay.com/wepay.min.js", wePayLoadedCallback);

  const [validationIsReady, setValidationIsReady] = useState(false);

  const [paymentMethodData, dispatch] = useCreditCardEntryState();
  const {
    ccNumber,
    cvc,
    month,
    year,
    name,
    zip,
    cardType,
    displayErrorMessages,
    ccInputsValidated,
    canBeEdited,
    shouldRetokenize,
    paymentMethodID,
    last4,
  } = paymentMethodData;

  const cardTypeName = cardType?.shortname || "";

  const shouldUpdateCardWithoutRetokenizing = canBeEdited && !shouldRetokenize;

  const resetState = () => {
    dispatch({
      type: "Set Editing",
      payload: false,
    });
    dispatch({
      type: "Should Be Retokenized",
      payload: false,
    });
  };

  const tokenizePaymentMethod = () => {
    window.WePay.tokens.create(
      {
        resource: "payment_methods",
        payment_methods: {
          type: "credit_card",
          credit_card: {
            card_number: ccNumber.replace(/[^\d]/g, ""),
            expiration_month: month * 1,
            expiration_year: ("20" + year) * 1,
            cvv: cvc,
          },
        },
      },
      {},
      (response) => {
        setPaymentMethodTokenInfo({
          cardType: cardTypeName,
          paymentMethodToken: response.id,
          name: name.trim(),
          month,
          year,
          zip,
          last4: ccNumber.substr(-4),
          replaceExistingCard: shouldRetokenize,
          paymentMethodID,
        });
        resetState();
      }
    );
  };

  const triggerUpdateWithoutTokenizing = () => {
    setPaymentMethodTokenInfo({
      cardType: cardTypeName,
      paymentMethodToken: null,
      name: name.trim(),
      month,
      year,
      zip,
      last4,
      paymentMethodID,
    });
    resetState();
  };

  useEffect(() => {
    if (getPaymentMethodTokenTrigger) setValidationIsReady(true);
  }, [getPaymentMethodTokenTrigger]);

  const clearValidationFlags = () => {
    setValidationIsReady(false);
    dispatch("Clear Display Errors");
  };

  const exclusions = shouldUpdateCardWithoutRetokenizing
    ? ["card_type", "card_number", "expiration_date", "cvc"]
    : [];

  useCCValidations(
    {
      ccNumber,
      cvc,
      month,
      year,
      name,
      zip,
      cardType,
    },
    validationIsReady,
    () => {
      clearValidationFlags();
      setInputErrors([]);
      dispatch({
        type: "Toggle CC Inputs Validated",
        payload: true,
      });
    },
    (errors) => {
      clearValidationFlags();
      setInputErrors(errors);
      setPaymentMethodTokenInfo({ error: true });
    },
    exclusions
  );

  useEffect(() => {
    if (ccInputsValidated) {
      if (displayErrorMessages.length > 0)
        return setPaymentMethodTokenInfo({ error: true });

      if (shouldUpdateCardWithoutRetokenizing) triggerUpdateWithoutTokenizing();
      else tokenizePaymentMethod();

      dispatch({
        type: "Toggle CC Inputs Validated",
        payload: false,
      });
    }
  }, [
    displayErrorMessages.length,
    setPaymentMethodTokenInfo,
    ccInputsValidated,
    dispatch,
    shouldUpdateCardWithoutRetokenizing,
  ]);

  return children;
}
